import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useChangeEmailApi } from '../api/use-change-email';
import { handleFormErrors } from '../../form/form-errors';
// User Change Email Schema
const userChangeEmailFormSchema = z.object({
    id: z.string(),
    email: z.string().min(1, 'too short').email('Invalid email'),
    password: z.string().min(5, 'too short'),
});
export const useChangeEmailForm = ({ user, onSuccess }) => {
    const defaultValues = useMemo(() => toFormData(user), [user]);
    const form = useForm({
        resolver: zodResolver(userChangeEmailFormSchema),
        defaultValues,
    });
    // reset form when user changes
    useEffect(() => {
        form.reset(toFormData(user));
    }, [user, form]);
    // mutations
    const { mutate: updateUser, isPending: isUpdatePending } = useChangeEmailApi({
        onSuccess,
        onError: (errors) => {
            handleFormErrors(form, errors);
        },
    });
    /**
     * Handles the form submission event, building the API payload and
     * invoking the update user mutation.
     *
     * @param values - Sanitized user update form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values);
        updateUser(payload);
    }, [updateUser]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isUpdatePending,
    };
};
/**
 * Compute the initial values for the UserUpdateForm.
 *
 * @param user - The current user object
 * @returns A fully populated UserUpdateForm with user data and defaults
 */
function toFormData(user) {
    return {
        id: user.id,
        email: user.email,
        password: '',
    };
}
/**
 * Build the UserRequestUpdate payload from form values.
 *
 * @param formValues - The values from the user update form
 * @returns A fully populated UserRequestUpdate for the update operation
 */
function toRequestData(formValues) {
    return {
        id: formValues.id,
        email: formValues.email.trim().toLowerCase(),
        password: formValues.password,
    };
}
